import { usePageTitle } from "@/hooks/usePageTitle";
import { useEffect, useState, useCallback, useRef } from "react";
import { useParams, Navigate } from "react-router-dom";
import { MessageCircle } from "lucide-react";
import { NavBar } from "@/components/NavBar";
import { ConversationList } from "@/components/chat/ConversationList";
import { ChatThread } from "@/components/chat/ChatThread";
import { listConversations, getConversation } from "@/lib/chat";
import { useAuth } from "@/context/AuthContext";

export default function Messages() {
  usePageTitle("Messages");
  const { convId } = useParams();
  const { user, loading: authLoading } = useAuth();
  const [convs, setConvs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);

  const refresh = useCallback(() => listConversations().then(setConvs).catch(() => {}).finally(() => setLoading(false)), []);

  useEffect(() => {
    if (!user) return;
    refresh();
    const t = setInterval(refresh, 15000);
    return () => clearInterval(t);
  }, [user, refresh]);

  const activeRef = useRef(null);
  activeRef.current = active;

  useEffect(() => {
    if (!convId || !user) return setActive(null);
    const found = convs.find((c) => c.id === convId);
    const cur = activeRef.current;
    if (found) return setActive(cur?.id === convId ? { ...cur, ...found } : found);
    // Keep the open chat on screen; only look it up if we don't have it yet
    if (cur?.id === convId) return;
    setActive(null);
    getConversation(convId)
      .then((d) => setActive({ ...(d?.conversation || d), id: convId }))
      .catch(() => setActive((c) => (c?.id === convId ? c : { id: convId })));
  }, [convId, convs, user]);

  if (!authLoading && !user) return <Navigate to="/login" replace />;

  return (
    <div data-testid="messages-page" className="bg-[#FBF9F5] h-[100dvh] flex flex-col">
      <NavBar />
      <main className="flex-1 min-h-0 pt-[104px] md:pt-16 lg:pt-20 flex">
        <aside className={`${convId ? "hidden lg:flex" : "flex"} flex-col w-full lg:w-[380px] shrink-0 border-r border-[#C5A059]/25 bg-white min-h-0`}>
          <div className="px-5 py-6 border-b border-[#C5A059]/25">
            <p className="eyebrow mb-2">Inbox</p>
            <h1 className="font-display text-3xl">Messages</h1>
          </div>
          <div className="flex-1 overflow-y-auto">
            <ConversationList items={convs} activeId={convId} userId={user?.user_id} loading={loading} />
          </div>
        </aside>
        <section className={`${convId ? "flex" : "hidden lg:flex"} flex-1 min-w-0 min-h-0 flex-col`}>
          {active ? (
            <ChatThread key={active.id} conv={active} userId={user?.user_id || user?.id} onActivity={refresh} />
          ) : (
            <div data-testid="chat-placeholder" className="flex-1 flex flex-col items-center justify-center text-center px-6">
              <MessageCircle className="h-10 w-10 text-[#C5A059] mb-4" strokeWidth={1} />
              <p className="font-display text-2xl">{convId ? "Loading chat…" : "Select a conversation"}</p>
            </div>
          )}
        </section>
      </main>
    </div>
  );
}
